import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { createSelector, select, Store } from "@ngrx/store";
import { AppState } from "../store";
import {
  arePricingPlansReady,
  isLoggedIn,
  isLoggedOut,
  isTeamManager,
  selectActiveCourse,
  selectAllCourses,
  selectTenantInfo,
  selectUser,
  selectUserPermissions,
  userPictureUrl,
} from "../store/selectors";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { environment } from "../../environments/environment";
import { TenantService } from "../services/tenant.service";
import {
  checkIfPlatformSite,
  checkIfSingleSignOnPage,
  goToLoginPage,
} from "../common/platform-utils";
import { isAnonymousUser, User } from "../models/user.model";
import { MatDialog } from "@angular/material/dialog";
import { NewsletterDialogComponent } from "../newsletter-dialog/newsletter-dialog.component";
import { UiEventsService } from "../services/ui-events.service";
import { MenuEntry, TenantInfo } from "../models/tenant.model";
import { createDefaultDialogConfig } from "../common/dialog-utils";
import { LoadingService } from "../shared/services/loading.service";
import { ResponsiveLayoutService } from "../services/responsive-layout.service";
import { TranslateModule } from "@ngx-translate/core";
import { AllowRoleDirective } from "../shared/auth/allow-role.directive";
import { AllowPlanDirective } from "../shared/allow-plan/allow-plan.directive";
import { DenyRoleDirective } from "../shared/auth/deny-role.directive";
import { AiGlobalChatService } from "../ai-global-chat/ai-global-chat.service";
import { MessagesService } from "../shared/services/messages.service";
import { TrialExpirationComponent } from "../shared/trial-expiration/trial-expiration.component";
import { Auth } from "@angular/fire/auth";
import { UserPermissions } from "../models/user-permissions.model";
import { TopMenuSvgSpriteComponent } from "./top-menu-svg-sprite.component";
import { NotificationDialogComponent } from "./notification-dialog/notification-dialog.component";
import { loadStripeJs } from "../shared/utils/stripe-loader";
import { UserNotificationsService } from "../services/user-notifications.service";
import { userDetailsUpdated } from "../store/user.actions";
import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { SitePagesService } from "../page-builder/site-pages/services/site-pages.service";
import { CustomPage } from "../models/page-builder/custom-page.model";
import { isLifetimeCustomer } from '../common/appsumo-utils';
import { createInstructorPlanDialog } from '../admin/school-settings/change-instructor-plan-dialog/change-instructor-plan-dialog.component';
import { MatTooltipModule } from "@angular/material/tooltip";
import { toSignal } from '@angular/core/rxjs-interop';
import { concatMap, filter, map, tap } from 'rxjs/operators';
import { InstructorPlanService } from '../admin/school-settings/instructor-plan.service';
import { GlobalAiIconSvgComponent } from '../svg-sprites/general/global-ai-icon-svg.component';
import { UserActivityLogService } from '../services/user-activity-log.service';
import { FeatureFlagService } from '../shared/services/feature-flag.service';
import { CookieService } from 'ngx-cookie-service';
import { UserPermissionsService } from '../shared/services/user-permissions.service';
import { PermissionKeys } from '../shared/services/permissions-key.enum';
import { CloseIconSvgComponent } from '../svg-sprites/general/close-icon-svg.component';
import { OchHeaderComponent } from '../header-footer-revamp/och-header.component';
import { RevampDesignService } from '../header-footer-revamp/revamp-design.service';
import { OchContinueWatching, OchHeaderVm, OchNavLink, OchSearchHit, hasRevampAdminRole, resolveRevampBrand } from '../header-footer-revamp/revamp.models';
import { calculateTrialDaysLeft, isTrialExpired } from '../common/trial-utils';
import { isTrialWithoutCardActive, NUMBER_OF_FREE_TRIAL_DAYS } from '../common/ui-constants';
import { isPlanAllowed } from '../shared/allow-plan/is-plan-allowed';
import { navigateToLesson } from '../store/latest-lesson.actions';

interface TopMenuData {
  tenant: TenantInfo;
  isLoggedIn: boolean;
  isLoggedOut: boolean;
  pictureUrl: string;
  arePricingPlansReady: boolean;
  user: User;
  showSubscription: boolean;
  isTeamManager: boolean;
  permissions: UserPermissions;
}

const selectTopMenuData = createSelector(
  selectTenantInfo,
  isLoggedIn,
  isLoggedOut,
  userPictureUrl,
  arePricingPlansReady,
  selectUser,
  isTeamManager,
  selectUserPermissions,
  (tenant, isLoggedIn, isLoggedOut, pictureUrl, arePricingPlansReady, user, isTeamManager, permissions) => ({
    tenant,
    isLoggedIn,
    isLoggedOut,
    pictureUrl,
    arePricingPlansReady,
    user,
    isTeamManager,
    showSubscription: permissions.isAdmin || (arePricingPlansReady && tenant?.isConnectedToStripe),
    permissions,
  })
);

@Component({
  selector: "top-menu",
  templateUrl: "./top-menu.component.html",
  styleUrls: ["./top-menu.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:popstate)': 'onPopState()',
    '(document:click)': 'clickout($event)',
    '(window:resize)': 'onResize()',
  },
  imports: [
    RouterLink,
    RouterLinkActive,
    DenyRoleDirective,
    AllowPlanDirective,
    AllowRoleDirective,
    TranslateModule,
    TrialExpirationComponent,
    TopMenuSvgSpriteComponent,
    NotificationDialogComponent,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    GlobalAiIconSvgComponent,
    CloseIconSvgComponent,
    OchHeaderComponent,
  ],
})
export class TopMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject<Store<AppState>>(Store);
  private afAuth = inject(Auth);
  private router = inject(Router);
  private loading = inject(LoadingService);
  private messages = inject(MessagesService);
  private tenant = inject(TenantService);
  private dialog = inject(MatDialog);
  private uiEvents = inject(UiEventsService);
  private aiChatService = inject(AiGlobalChatService);
  private responsive = inject(ResponsiveLayoutService);
  private elementRef = inject(ElementRef);
  private userNotificationsService = inject(UserNotificationsService);
  private sitePagesService = inject(SitePagesService);
  private userActivityLogService = inject(UserActivityLogService);
  featureFlagService = inject(FeatureFlagService);
  private cookies = inject(CookieService);
  private userPermissions = inject(UserPermissionsService);
  instructorPlanService = inject(InstructorPlanService);

  // ViewChild queries
  readonly navBarLeftMenu = viewChild("navBarLeftMenu", { read: ElementRef });
  readonly navBarRightMenu = viewChild("navBarRightMenu", { read: ElementRef });
  readonly navBarContainer = viewChild('navBarContainer', { read: ElementRef });

  // State signals
  readonly isOpen = signal(false);
  readonly isOpenNotification = signal(false);
  readonly firstExtraMenuEntries = signal<MenuEntry[]>([]);
  readonly moreMenuEntries = signal<MenuEntry[]>([]);

  // Derived state
  readonly dataSig = toSignal(this.store.pipe(select(selectTopMenuData)));
  private readonly tenantInfoSig = toSignal(this.store.pipe(select(selectTenantInfo)));
  // Active course drives the header progress bar (only set on course/watch pages).
  private readonly activeCourseSig = toSignal(this.store.pipe(select(selectActiveCourse)));
  private readonly allCoursesSig = toSignal(this.store.pipe(select(selectAllCourses)), { initialValue: [] });

  // Continue-watching card: lazily loaded once when the account menu first opens,
  // then cached for the session (no read on page load).
  readonly continueWatching = signal<OchContinueWatching | null>(null);
  private continueWatchingLoaded = false;
  readonly isFullScreenMode = toSignal(this.uiEvents.fullScreen$, { initialValue: false });
  readonly isPhonePortrait = toSignal(this.responsive.isMobilePortait$, { initialValue: false });
  readonly customHomePageAvailable = toSignal<CustomPage | undefined>(this.sitePagesService.homePage$, { initialValue: undefined });
  readonly isLandingPageMode = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() =>
        (window.location.search.includes('couponCode') || window.location.search.includes('bundleCode')) &&
        !window.location.search.includes('purchaseResult')
      )
    ),
    { initialValue: false }
  );
  // True only on a course sales page or a watch page. Gates the header progress
  // bar by ROUTE — activeCourseId is set-only (never reset platform-wide), so we
  // must not rely on it to know we've left the course.
  private readonly onCourseRouteSig = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.isCourseRoute())
    ),
    { initialValue: this.isCourseRoute() }
  );

  readonly revampDesign = inject(RevampDesignService);
  readonly useNewDesign = this.revampDesign.useNewDesign;

  /** Nav model for the revamped header — same visibility rules as the classic template. */
  readonly headerNavLinks = computed<OchNavLink[]>(() => {
    const data = this.dataSig();
    if (!data?.tenant) return [];
    const links: OchNavLink[] = [];
    if (data.tenant.homePageOn) {
      links.push({ id: 'home', labelKey: 'menu.home', routerLink: '/home' });
    }
    links.push({ id: 'courses', labelKey: 'menu.products', routerLink: '/courses' });
    if (this.isCommunityVisible(data)) {
      links.push({ id: 'community', labelKey: 'community.common.community', routerLink: '/community' });
    }
    if (!data.tenant.allLessonsTabOff) {
      links.push({ id: 'all-lessons', labelKey: 'menu.allLessons', routerLink: '/all-lessons' });
    }
    if (data.showSubscription && !data.tenant.subscriptionScreenDisabled) {
      links.push({ id: 'subscription', labelKey: 'menu.subscription', routerLink: '/subscription' });
    }
    if (this.isPageBuilderVisible(data)) {
      links.push({ id: 'page-builder', labelKey: 'menu.pageBuilder', routerLink: '/page-builder/site-pages' });
    }
    if (!data.tenant.contactPageDisabled) {
      links.push({ id: 'contact', labelKey: 'menu.contact', routerLink: '/contact' });
    }
    if (this.isNewsletterMenuVisible(data)) {
      links.push({ id: 'newsletter', labelKey: 'menu.newsLetter' });
    }
    if (this.isAdminMenuAllowed(data)) {
      links.push({ id: 'admin', labelKey: 'menu.admin', routerLink: this.adminMenuLink(data) });
    }
    (data.tenant.customMenu ?? []).forEach((menuEntry, index) => {
      links.push({
        id: `custom-${index}-${menuEntry?.title ?? ''}`,
        label: menuEntry?.title ?? '',
        href: menuEntry?.link,
        target: menuEntry?.targetTabState ? '_blank' : '_self',
        custom: true,
      });
    });
    return links;
  });

  readonly headerVm = computed<OchHeaderVm | null>(() => {
    const data = this.dataSig();
    const tenant = data?.tenant;
    if (!tenant) return null;
    const brand = resolveRevampBrand(
      tenant.brandTheme,
      tenant.customFooterConfig?.footerColor
    );
    const schoolName = tenant.schoolName ?? '';
    const showNav = !this.isSingleSignOnPage && !this.isLandingPageMode();
    const showTrial =
      tenant.instructorPricingPlan === 'growth' && !isTrialExpired(tenant) && isTrialWithoutCardActive();
    const trialDaysLeft = showTrial ? Math.max(calculateTrialDaysLeft(tenant), 0) : 0;
    return {
      schoolName,
      logoUrl: tenant.topMenuLogoConfig?.topMenuLogoUrl || undefined,
      logoLetter: (schoolName.trim().charAt(0) || 'S').toUpperCase(),
      primary: brand.primary,
      accent: brand.accent,
      footer: brand.footer,
      showNav,
      showBurger: showNav && !this.isTenantLoginPage,
      loggedIn: !!data.isLoggedIn,
      loggedOut: !!data.isLoggedOut,
      avatarUrl: data.pictureUrl || undefined,
      avatarInitials: this.buildAvatarInitials(data.user),
      userName: data.user?.displayName || data.user?.email || '',
      userEmail: data.user?.email ?? '',
      isStudent: !!data.isLoggedIn && !this.hasAdminRole(data.permissions),
      showMyCertificates: !tenant.disableMyCertificatesScreen,
      showAffiliateProgram: !!tenant.affiliateProgramSettings?.enabled,
      showCreateYourCourse: !tenant.platformLabelling?.disableCreateYourCourseStudentMenu,
      showTrial,
      trialDaysLeft,
      trialPercent: Math.round((trialDaysLeft / NUMBER_OF_FREE_TRIAL_DAYS) * 100),
      showUpgrade: this.isUpgradeButtonVisible(data),
      showAiAssistant: this.hasAdminRole(data.permissions),
      showNotifications: this.isCommunityVisible(data),
      unreadCount: data.user?.unreadNotificationsCounter ?? 0,
      showTestPill: !!(data.isLoggedIn && data.user?.testUser),
      courseProgress: this.resolveCourseProgress(data.user),
      showSearch: showNav && !this.isPlatformSite,
    };
  });

  // Set once, not reactive
  readonly isPlatformSite: boolean;
  readonly isSingleSignOnPage: boolean;
  readonly isTenantLoginPage: boolean;
  readonly environment: unknown;

  private resizeObserver: ResizeObserver;
  private resizeTimeout: ReturnType<typeof setTimeout>;

  constructor() {
    this.isPlatformSite = checkIfPlatformSite();
    this.isSingleSignOnPage = checkIfSingleSignOnPage();
    this.isTenantLoginPage = document.location.pathname.endsWith("login-page");
    this.environment = environment;

    // Recalc menu layout on tenant/view-ref changes and (re)attach the observer to
    // the current classic-branch elements — needed because toggling new→classic at
    // runtime recreates that DOM after ngAfterViewInit. scheduleRecalculation() defers
    // via debounce + double-RAF so measurements read post-paint (avoids flicker).
    effect(() => {
      const tenant = this.tenantInfoSig();
      const leftMenu = this.navBarLeftMenu();
      const rightMenu = this.navBarRightMenu();
      const container = this.navBarContainer();

      this.resizeObserver?.disconnect();

      if (!leftMenu?.nativeElement || !rightMenu?.nativeElement) return;

      // Suppress unused-variable warnings — reads are needed to register reactive deps.
      void tenant;

      this.resizeObserver ??= new ResizeObserver(() => this.scheduleRecalculation());
      // Intentionally not observing navBarLeftMenu: its size changes when we add/remove
      // items from it, which would trigger a cascade of recalculations and flicker.
      if (container?.nativeElement) this.resizeObserver.observe(container.nativeElement);
      this.resizeObserver.observe(rightMenu.nativeElement);

      this.scheduleRecalculation();
    });

    // Redirect to purchase complete page if a pending purchase session cookie is present
    effect(() => {
      const purchaseSessionId = this.cookies.get("purchaseSessionId");
      if (!purchaseSessionId) return;
      const data = this.dataSig();
      if (data?.isLoggedIn && window.location.pathname !== '/purchasecomplete') {
        window.location.href = "/purchasecomplete";
      }
    });
  }

  async ngOnInit() {
    await this.sitePagesService.getHomePage();
  }

  ngAfterViewInit(): void {
    // Observer attachment + initial layout are handled by the constructor effect above,
    // which reacts to the view-ref signals (they resolve after view init) and re-attaches
    // if the classic branch is (re)created at runtime.
    requestAnimationFrame(() => this.recalculateMenuEntries());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  onPopState() {
    this.aiChatService.endGlobalAiChatSession();
  }

  clickout(event: Event) {
    if (!this.elementRef.nativeElement.contains((event as MouseEvent).target)) {
      this.isOpenNotification.set(false);
    }
  }

  onResize() {
    this.scheduleRecalculation();
  }

  private scheduleRecalculation() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.recalculateMenuEntries());
      });
    }, 50);
  }

  private layoutMenuEntries(leftEl: HTMLElement, rightEl: HTMLElement, menuEntries: MenuEntry[]) {
    const firstExtraMenuEntries: MenuEntry[] = [];
    const moreMenuEntries: MenuEntry[] = [];

    const leftEdge = leftEl.getBoundingClientRect().right;
    const rightEdge = rightEl.getBoundingClientRect().left;
    let widthAvailable = rightEdge - leftEdge - 50;

    for (const menuEntry of menuEntries) {
      const menuEntryWidth = (menuEntry?.title?.length ?? 10) * 8 + 40;
      if (widthAvailable > menuEntryWidth) {
        firstExtraMenuEntries.push(menuEntry);
        widthAvailable -= menuEntryWidth;
      } else {
        moreMenuEntries.push(menuEntry);
      }
    }

    this.firstExtraMenuEntries.set(firstExtraMenuEntries);
    this.moreMenuEntries.set(moreMenuEntries);
  }

  private recalculateMenuEntries() {
    const leftEl = this.navBarLeftMenu?.()?.nativeElement;
    const rightEl = this.navBarRightMenu?.()?.nativeElement;

    if (!leftEl || !rightEl) return;

    this.layoutMenuEntries(leftEl, rightEl, this.tenantInfoSig()?.customMenu ?? []);
  }

  toggleMenu() {
    this.isOpen.update(v => !v);
  }

  login(startInCoursesPage = false, _tenantInfo: TenantInfo) {
    if (document.location.pathname?.endsWith("login-page")) {
      return;
    }
    goToLoginPage(this.router, startInCoursesPage, this.tenant.id);
  }

  async logout(_tenantInfo: TenantInfo, userId: string) {
    await this.userActivityLogService.reportUserLogout(userId, this.tenant.id);
    this.afAuth.signOut().then(() => {
      this.loading.loadingOn();
      window.location.href = "/courses";
    });
  }

  async toggleNotificationDialog(userId: string, unreadNotificationsCounter: number) {
    try {
      this.isOpenNotification.update(v => !v);

      if (this.isOpenNotification() && unreadNotificationsCounter > 0) {
        await this.userNotificationsService.zeroOutNotificationsCounter(userId);
        this.store.dispatch(userDetailsUpdated({ userId, changes: { unreadNotificationsCounter: 0 } }));
      }
    } catch (e) {
      this.isOpenNotification.update(v => !v);
      this.messages.error(e);
    }
  }

  showNewsletterPopup() {
    const dialogConfig = createDefaultDialogConfig();
    dialogConfig.width = "710px";
    this.dialog.open(NewsletterDialogComponent, dialogConfig);
  }

  joinPartnerProgram() {
    window.open("https://instructor-academy.onlinecoursehost.com/partner-program/", "_blank");
  }

  openHelpCenter() {
    window.open("https://help.onlinecoursehost.com", "_blank");
  }

  closeAiGlobalChat() {
    this.aiChatService.endGlobalAiChatSession();
  }

  toggleAiChat(event: Event, tenant: TenantInfo) {
    if (tenant?.instructorPricingPlan == "growth") {
      this.messages.info(`The AI Course Creation Assistant is only available if you have a paid plan.`);
      return;
    }
    this.aiChatService.toggleAiChat();
    event.stopPropagation();
  }

  isAdminMenuAllowed(data: TopMenuData) {
    return (
      data.permissions.isAdmin ||
      data.permissions.isPlatformAdmin ||
      this.userPermissions.can('Admin') ||
      data.permissions.isCourseAdmin
    );
  }

  showOnBoardingTutorials(data: TopMenuData) {
    return data.permissions.isPlatformAdmin || data.permissions.isAdmin;
  }

  isCommunityVisible(data: TopMenuData) {
    if (!environment?.features?.community) {
      return false;
    }
    if (data.user?.communityMemberSettings?.banned) {
      return false;
    }
    if (data?.permissions?.isPlatformAdmin) {
      return true;
    }
    if (data?.permissions?.isAdmin || data?.permissions?.isCourseAdmin) {
      return true;
    }
    // if it's a student user and a community is configured (paid or free) and not explicitly disabled
    if (
      !data?.permissions?.isAdmin &&
      !data?.permissions?.isCourseAdmin &&
      data?.tenant?.communitySettings?.communityAccess &&
      data?.tenant?.communitySettings?.disabled !== true
    ) {
      return true;
    }
    if (
      isAnonymousUser(data?.user) &&
      data?.tenant?.communitySettings?.communityAccess == "free" &&
      data?.tenant?.communitySettings?.disabled == false
    ) {
      return true;
    }
    return false;
  }

  isPageBuilderVisible(data: TopMenuData) {
    const active = environment?.features?.pageBuilder || data?.permissions?.isPlatformAdmin;
    const hasPermission = this.userPermissions.can(PermissionKeys.PageBuilder);
    return active && (data?.permissions?.isAdmin || hasPermission);
  }

  isUpgradeButtonVisible(data: TopMenuData) {
    if (isAnonymousUser(data.user)) {
      return false;
    }
    if (!data.permissions.isAdmin) {
      return false;
    }
    if (data.tenant?.instructorPricingPlan == "unlimited") {
      return false;
    }
    if (data.tenant?.instructorPricingPlan == "growth") {
      return false;
    }
    return environment?.features?.upgradeButton;
  }

  async onUpgradeBtnClicked(data: TopMenuData) {
    if (isLifetimeCustomer(data.tenant)) {
      await this.router.navigateByUrl("/upgrade-to-unlimited");
    } else {
      createInstructorPlanDialog(this.dialog)
        .pipe(
          concatMap(plan => this.loading.showLoaderUntilCompleted(
            this.instructorPlanService.updateInstructorPlan(
              false,
              this.tenant.tenant['email'],
              plan?.selectedInstructorPlanId,
              plan?.selectedPlanFrequency
            )
          )),
          tap(async (response) => {
            const StripeConstructor = await loadStripeJs();
            const stripe = StripeConstructor(response.stripePublicKey);
            stripe.redirectToCheckout({ sessionId: response.sessionId });
          })
        )
        .subscribe();
    }
  }

  closeNotificationDialog() {
    this.isOpenNotification.set(false);
  }

  evaluateCustomMenuHref(customMenu: MenuEntry) {
    if (customMenu?.['dynamic']) {
      if (customMenu?.link) {
        const { pathname } = new URL(customMenu.link);
        const origin = location.origin;
        if (pathname.split('/').filter(Boolean)[0] === 'pages') {
          return origin + pathname;
        }
      }
    }
    return customMenu?.link;
  }

  adminMenuLink(data: TopMenuData): string {
    if (data.permissions.isCourseAdmin) {
      return '/admin/settings/manage-students';
    }
    return this.userPermissions.redirectUserAccordingToPermissions();
  }

  onHeaderNavAction(link: OchNavLink) {
    if (link.id === 'newsletter') {
      this.showNewsletterPopup();
    }
  }

  /** Route to the selected lesson search hit (same action the all-lessons search uses). */
  onHeaderSearchSelected(searchResult: OchSearchHit) {
    if (!searchResult?.courseId) return;
    this.store.dispatch(navigateToLesson({
      courseId: searchResult.courseId,
      sectionId: searchResult.sectionId,
      seqNo: searchResult.seqNo,
    }));
  }

  private hasAdminRole(permissions: UserPermissions): boolean {
    return hasRevampAdminRole(permissions);
  }

  /**
   * Lazy-load the "Continue watching" card the first time the account menu opens.
   * One-shot limit(1) Firestore read, cached for the session; never runs on page
   * load and never re-queries. Silently no-ops if there's no data or no user.
   */
  async onAccountMenuOpened() {
    if (this.continueWatchingLoaded) return;
    this.continueWatchingLoaded = true;

    const userId = this.dataSig()?.user?.id;
    if (!userId) return;

    const doc = await this.userActivityLogService.loadMostRecentActivity(userId);
    if (!doc?.courseId) return;

    const course = this.allCoursesSig().find(c => c.id === doc.courseId);
    if (!course?.url) return; // can't build a real link → hide the card

    const progressMatrix = this.dataSig()?.user?.progressMatrix;
    const rawPct = progressMatrix?.[doc.courseId];
    const percent = rawPct == null || isNaN(rawPct) ? null : Math.max(0, Math.min(100, Math.round(rawPct)));

    this.continueWatching.set({
      routerLink: `/courses/${course.url}`,
      courseTitle: course.title ?? '',
      percent,
    });
  }

  /**
   * Header progress bar %: the logged-in user's persisted completion for the
   * active course (User.progressMatrix), or null when not on a course / no data.
   * Reads only already-loaded store data — no extra Firestore reads.
   */
  private resolveCourseProgress(user: User): number | null {
    // Only on a course/watch route — activeCourseId is sticky (never reset), so
    // without this the bar would linger on /home, /courses, etc. after one visit.
    if (!this.onCourseRouteSig()) return null;
    const courseId = this.activeCourseSig()?.id;
    if (!courseId || !user?.progressMatrix) return null;
    const pct = user.progressMatrix[courseId];
    if (pct == null || isNaN(pct)) return null;
    return Math.max(0, Math.min(100, Math.round(pct)));
  }

  /** /courses/:url (sales page) or /courses/:url/:section/lessons/:lesson (watch page). */
  private isCourseRoute(): boolean {
    const parts = this.router.url.split('?')[0].split('/').filter(Boolean);
    return parts[0] === 'courses' && (parts.length === 2 || (parts.length === 5 && parts[3] === 'lessons'));
  }

  /** Same gate as the classic newsletter item: *denyRole ADMIN + form enabled + *allowPlan basic. */
  private isNewsletterMenuVisible(data: TopMenuData): boolean {
    return (
      !this.hasAdminRole(data.permissions) &&
      !data?.tenant?.newsletter?.newsletterFormDisabled &&
      isPlanAllowed(data.tenant, 'basic')
    );
  }

  private buildAvatarInitials(user: User): string {
    const name = user?.displayName?.trim();
    if (name) {
      const parts = name.split(/\s+/);
      const first = parts[0]?.charAt(0) ?? '';
      const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
      return (first + last).toUpperCase() || '?';
    }
    return (user?.email?.charAt(0) ?? '?').toUpperCase();
  }
}
